import { BuildingsIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type LinkedInMention, useLinkedInTags } from '@/hooks/useLinkedInTags';
import { FIELD_LABEL } from '@/lib/ui-tokens';

export interface LinkedInTagsFieldProps {
  postId?: string;
  caption: string;
  value: LinkedInMention[];
  onChange: (next: LinkedInMention[]) => void;
}

function CompanyLogo({ mention }: { mention: LinkedInMention }) {
  return (
    <Avatar className="size-6 rounded-md">
      {mention.logoUrl ? <AvatarImage src={mention.logoUrl} alt="" /> : null}
      <AvatarFallback className="rounded-md">
        <BuildingsIcon className="size-3.5 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
}

export function LinkedInTagsField({ postId, caption, value, onChange }: LinkedInTagsFieldProps) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');
  const tags = useLinkedInTags(postId);

  const tagged = new Set(value.map((m) => m.urn));
  const recent = tags.recent.filter((m) => !tagged.has(m.urn)).slice(0, 5);

  const commit = (next: LinkedInMention[]) => {
    onChange(next);
    tags.save(next);
  };

  const add = (mention: LinkedInMention) => {
    if (!tagged.has(mention.urn)) commit([...value, mention]);
    setLink('');
    tags.resetLookup();
    setOpen(false);
  };

  const result = tags.lookupResult;

  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Tagged on LinkedIn</span>

      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {value.map((mention) => {
            const inCaption = caption.includes(mention.name);
            return (
              <li
                key={mention.urn}
                className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1.5 pr-1"
              >
                <CompanyLogo mention={mention} />
                <span className="flex flex-col leading-tight">
                  <span className="text-caption font-medium text-foreground">{mention.name}</span>
                  {inCaption ? null : (
                    <span className="text-caption text-muted-foreground">Not in the caption</span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="flat"
                  size="icon-sm"
                  aria-label={`Remove ${mention.name}`}
                  onClick={() => commit(value.filter((m) => m.urn !== mention.urn))}
                >
                  <XIcon className="size-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setLink('');
            tags.resetLookup();
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button type="button" variant="flat" size="sm" className="self-start">
            <PlusIcon className="size-3.5" />
            Tag a company
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-80 flex-col gap-3">
          <form
            className="flex flex-col gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              if (link.trim()) tags.lookup(link.trim());
            }}
          >
            <label htmlFor="linkedin-company-link" className={FIELD_LABEL}>
              Paste the company page link from LinkedIn
            </label>
            <Input
              id="linkedin-company-link"
              value={link}
              placeholder="linkedin.com/company/…"
              onChange={(event) => {
                setLink(event.target.value);
                if (result || tags.lookupError) tags.resetLookup();
              }}
            />
          </form>

          {tags.isLookingUp ? (
            <p className="text-caption text-muted-foreground">Looking up the page…</p>
          ) : null}
          {result?.state === 'found' ? (
            <div className="flex items-center gap-2">
              <CompanyLogo mention={result.mention} />
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-sm font-medium text-foreground">
                  {result.mention.name}
                </span>
                <span className="text-caption text-muted-foreground">Found on LinkedIn</span>
              </span>
              <Button type="button" size="sm" onClick={() => add(result.mention)}>
                Tag
              </Button>
            </div>
          ) : null}
          {result?.state === 'not_found' ? (
            <p className="text-caption text-muted-foreground">
              LinkedIn did not return a company page for that link.
            </p>
          ) : null}
          {result?.state === 'invalid_link' ? (
            <p className="text-caption text-muted-foreground">
              That is not a company page link. It should look like linkedin.com/company/name.
            </p>
          ) : null}
          {result?.state === 'not_connected' ? (
            <p className="text-caption text-muted-foreground">
              Connect LinkedIn in Settings to look up company pages.
            </p>
          ) : null}
          {tags.lookupError ? (
            <p className="text-caption text-muted-foreground">
              The lookup did not finish. Try again in a moment.
            </p>
          ) : null}

          {recent.length > 0 ? (
            <div className="flex flex-col gap-1">
              <span className={FIELD_LABEL}>Recently tagged</span>
              {recent.map((mention) => (
                <button
                  key={mention.urn}
                  type="button"
                  className="flex items-center gap-2 rounded-md p-1 text-left hover:bg-accent"
                  onClick={() => add(mention)}
                >
                  <CompanyLogo mention={mention} />
                  <span className="truncate text-sm text-foreground">{mention.name}</span>
                </button>
              ))}
            </div>
          ) : null}

          <p className="text-caption text-muted-foreground">
            LinkedIn only lets Mora tag company pages.
          </p>
        </PopoverContent>
      </Popover>
    </div>
  );
}
